import { useMemo, useState } from "react";
import { CheckCircle2, Plus, RefreshCw, Search } from "lucide-react";

import { FormNotice } from "@/components/forms/FormNotice";
import { useAdminGrantCourseToDashboard } from "@/hooks/useAdminCourseAccess";
import { useBundleList } from "@/hooks/useBundles";
import { useCourseList } from "@/hooks/useCourses";
import { isPermissionExpired, useCoursePermissions } from "@/hooks/usePermissions";
import type { BundleWithCourses, CoursePermissionWithCourse, CourseWithDetails } from "@/lib/schema";

import { AdminPanelHeader } from "./AdminPanelHeader";

type GrantEntry =
  | {
      id: string;
      type: "bundle";
      label: "Signature Routine";
      name: string;
      description: string | null;
      bundle: BundleWithCourses;
    }
  | {
      id: string;
      type: "course";
      label: "Featured Dance" | "Dance Tutorial";
      name: string;
      description: string | null;
      course: CourseWithDetails;
    };

function sortByName<T extends { name: string }>(items: T[]) {
  return [...items].sort((left, right) => left.name.localeCompare(right.name));
}

function getCourseSearchText(course: CourseWithDetails) {
  return [
    course.name,
    course.short_description,
    course.long_description,
    course.Instructor?.name,
    ...(course.category ?? []).map((category) => category.name),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function getEntrySearchText(entry: GrantEntry) {
  if (entry.type === "bundle") {
    return [
      entry.name,
      entry.description,
      entry.bundle.long_description,
      ...entry.bundle.courses.map((course) => getCourseSearchText(course)),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
  }

  return getCourseSearchText(entry.course);
}

function getEntryCourseIds(entry: GrantEntry) {
  return entry.type === "bundle"
    ? entry.bundle.courses.map((course) => course.id)
    : [entry.course.id];
}

function getEntryStatus(entry: GrantEntry, activeCourseIds: Set<string>) {
  const courseIds = getEntryCourseIds(entry);
  const activeCount = courseIds.filter((courseId) => activeCourseIds.has(courseId)).length;

  if (courseIds.length === 0) {
    return { key: "empty", label: "No tutorials", added: false } as const;
  }

  if (activeCount === courseIds.length) {
    return { key: "added", label: "In My Dashboard", added: true } as const;
  }

  if (activeCount > 0) {
    return { key: "partial", label: `${activeCount}/${courseIds.length} added`, added: false } as const;
  }

  return { key: "not-added", label: "Not added", added: false } as const;
}

function getStatusClasses(statusKey: ReturnType<typeof getEntryStatus>["key"]) {
  if (statusKey === "added") return "bg-green-100 text-green-700";
  if (statusKey === "partial") return "bg-amber-100 text-amber-700";
  return "bg-gray-100 text-gray-600";
}

export function AdminCourseAccessPanel() {
  const coursesResult = useCourseList();
  const bundlesResult = useBundleList();
  const permissionsResult = useCoursePermissions();
  const grantCourse = useAdminGrantCourseToDashboard();

  const [query, setQuery] = useState("");
  const [addingEntryId, setAddingEntryId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "success" | "error"; message: string } | null>(null);
  const permissions = (permissionsResult.data ?? []) as CoursePermissionWithCourse[];
  const courses = (coursesResult.data ?? []) as CourseWithDetails[];
  const bundles = (bundlesResult.data ?? []) as BundleWithCourses[];

  const activeCourseIds = useMemo(() => {
    const ids = new Set<string>();

    for (const permission of permissions) {
      if (!isPermissionExpired(permission)) {
        ids.add(permission.course_id.id);
      }
    }

    return ids;
  }, [permissions]);

  const sortedEntries = useMemo<GrantEntry[]>(() => {
    const signatureRoutineEntries = sortByName(bundles).map((bundle) => ({
      id: `bundle:${bundle.id}`,
      type: "bundle" as const,
      label: "Signature Routine" as const,
      name: bundle.name,
      description: bundle.short_description,
      bundle,
    }));

    const featuredDanceEntries = sortByName(
      courses.filter((course) => !course.is_free && Boolean(course.most_popular)),
    ).map((course) => ({
      id: `course:${course.id}`,
      type: "course" as const,
      label: "Featured Dance" as const,
      name: course.name,
      description: course.short_description,
      course,
    }));

    const danceTutorialEntries = sortByName(
      courses.filter((course) => course.is_free || !course.most_popular),
    ).map((course) => ({
      id: `course:${course.id}`,
      type: "course" as const,
      label: "Dance Tutorial" as const,
      name: course.name,
      description: course.short_description,
      course,
    }));

    return [
      ...signatureRoutineEntries,
      ...featuredDanceEntries,
      ...danceTutorialEntries,
    ];
  }, [bundles, courses]);

  const filteredEntries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return sortedEntries;

    return sortedEntries.filter((entry) =>
      getEntrySearchText(entry).includes(normalizedQuery),
    );
  }, [query, sortedEntries]);

  const isRefreshing = coursesResult.loading || bundlesResult.loading || permissionsResult.loading;

  const refreshAll = () => {
    setNotice(null);
    void coursesResult.refetch();
    void bundlesResult.refetch();
    void permissionsResult.refetch();
  };

  const handleAddEntry = async (entry: GrantEntry) => {
    const courseIds = getEntryCourseIds(entry);
    if (courseIds.length === 0) {
      setNotice({ tone: "error", message: "This signature routine has no tutorials to add." });
      return;
    }

    setNotice(null);
    setAddingEntryId(entry.id);

    for (const courseId of courseIds) {
      const result = await grantCourse.execute(courseId);
      if (result.error) {
        setAddingEntryId(null);
        setNotice({ tone: "error", message: result.error });
        return;
      }
    }

    setAddingEntryId(null);
    await permissionsResult.refetch();
    setNotice({
      tone: "success",
      message: `${entry.name} was added to My Dashboard.`,
    });
  };

  return (
    <div className="space-y-6">
      <AdminPanelHeader
        inset
        title="My Library"
        description="Add any Dance Experience to your library without going through checkout. This does not create a Stripe purchase record."
        action={
          <button
            type="button"
            onClick={refreshAll}
            disabled={isRefreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-[#e5d7e0] bg-white px-4 py-2 text-sm font-semibold text-[#5d2449] transition-colors hover:bg-[#fbf4f8] disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        }
      />

      {notice ? (
        <FormNotice message={notice.message} tone={notice.tone} role={notice.tone === "success" ? "status" : "alert"} />
      ) : null}

      {coursesResult.error ? (
        <FormNotice message={`Could not load tutorials: ${coursesResult.error}`} />
      ) : null}

      {bundlesResult.error ? (
        <FormNotice message={`Could not load signature routines: ${bundlesResult.error}`} />
      ) : null}

      {permissionsResult.error ? (
        <FormNotice message={`Could not load your current dashboard tutorials: ${permissionsResult.error}`} />
      ) : null}

      <section className="rounded-[1.5rem] border border-gray-200 bg-white p-5 shadow-[0_14px_40px_rgba(31,10,24,0.05)]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Dance Experience Picker</h3>
            <p className="mt-1 text-sm text-gray-500">
              {activeCourseIds.size} Tutorials currently available in My Dashboard.
            </p>
          </div>

          <label className="relative block w-full sm:max-w-sm">
            <span className="sr-only">Search Dance Experiences</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search Dance Experiences"
              className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
            />
          </label>
        </div>

        <div className="mt-5 overflow-hidden rounded-xl border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[580px] text-left text-sm">
              <thead className="border-b bg-gray-50 text-xs uppercase tracking-[0.08em] text-gray-500">
                <tr>
                  <th className="w-[58%] px-4 py-3 text-left">Tutorial</th>
                  <th className="w-[20%] px-4 py-3 text-left">Status</th>
                  <th className="w-[22%] px-4 py-3 text-left">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isRefreshing ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-10 text-center text-gray-400">
                      Loading tutorials…
                    </td>
                  </tr>
                ) : filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-10 text-center text-gray-400">
                      {query.trim() ? "No tutorials match this search." : "No tutorials found."}
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry) => {
                    const status = getEntryStatus(entry, activeCourseIds);
                    const isAdding = addingEntryId === entry.id;

                    return (
                      <tr key={entry.id} className="align-top hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{entry.name}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span className="rounded-full bg-[#f6eef4] px-2 py-0.5 font-semibold text-[#762a5c]">
                              {entry.label}
                            </span>
                            {entry.type === "bundle" ? (
                              <span>
                                {entry.bundle.courses.length} tutorial{entry.bundle.courses.length === 1 ? "" : "s"}
                              </span>
                            ) : null}
                          </div>
                          {entry.description ? (
                            <p className="mt-1 line-clamp-2 max-w-[42rem] text-xs text-gray-500">
                              {entry.description}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClasses(status.key)}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => void handleAddEntry(entry)}
                            disabled={status.added || status.key === "empty" || grantCourse.loading}
                            className="inline-flex min-w-[10.75rem] items-center justify-center gap-1.5 rounded-full border border-[#762a5c] bg-white px-3 py-1.5 text-xs font-semibold text-[#762a5c] transition-colors hover:bg-[#faf1f6] disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-white"
                          >
                            {status.added ? (
                              <>
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Added
                              </>
                            ) : isAdding ? (
                              "Adding…"
                            ) : (
                              <>
                                <Plus className="h-3.5 w-3.5" />
                                Add to My Dashboard
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AdminCourseAccessPanel;
